
import React, { useEffect, useState, useContext } from 'react';
import './Sidebar.css';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { RefreshContext, AuthContext } from '../../../App'; // Import AuthContext and RefreshContext
import {
    IoChevronBack, IoChevronForward, IoGridOutline, IoCheckboxOutline,
    IoBriefcaseOutline, IoPeopleOutline, IoSettingsOutline, IoLogOutOutline,
    IoConstructOutline, IoChevronDown, IoLayersOutline,
    IoDocumentTextOutline, IoBuildOutline, IoFileTrayStackedOutline
} from 'react-icons/io5';
import { BsPersonGear } from 'react-icons/bs';
import { HiOutlineUsers, HiOutlineUserGroup } from 'react-icons/hi';

const API_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const GET_COMPANY_DETAILS_ENDPOINT = `${API_BASE_URL}/api/get-company`;
const GET_USERS_ENDPOINT = `${API_BASE_URL}/api/get-users`; // Added endpoint for user refresh
const AUTH_TOKEN_KEY = 'zTaskAuthToken';
const USER_DETAILS_KEY = 'zTaskUserDetails'; // Added key to update local storage if needed

const SidebarSkeleton = ({ isCollapsed }) => (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header"><div className={`skeleton-block ${isCollapsed ? 'logo-skeleton-collapsed' : 'logo-skeleton-expanded'}`}></div></div>
        <div className="sidebar-content"><div className="menu-group">{!isCollapsed && <div className="skeleton-line menu-title-skeleton"></div>}<ul>{Array.from({ length: 5 }).map((_, index) => (<li key={index} className="skeleton-menu-item"><div className="skeleton-block icon-skeleton-sidebar"></div>{!isCollapsed && <div className="skeleton-line text-skeleton-sidebar"></div>}</li>))}</ul></div></div>
        <div className="sidebar-footer"><div className="skeleton-user-profile"><div className="skeleton-block avatar-skeleton"></div>{!isCollapsed && (<div className="skeleton-user-info"><div className="skeleton-line user-name-skeleton"></div><div className="skeleton-line user-subtext-skeleton"></div></div>)}</div></div>
    </div>
);

const Sidebar = ({ refreshKey }) => {
    // --- State and Context Hooks ---
    const location = useLocation();
    const { triggerRefresh } = useContext(RefreshContext);
    const { user } = useContext(AuthContext); 

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    
    // Local state for user info to allow immediate UI updates without full page reload
    const [userInfo, setUserInfo] = useState(user);

    // State specifically for company details
    const [isCompanyLoading, setIsCompanyLoading] = useState(true);
    const [companyName, setCompanyName] = useState('Loading...');
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
    const [companyWebsiteUrl, setCompanyWebsiteUrl] = useState('#');
    const [companyKeyword, setCompanyKeyword] = useState(null);

    // Sync local state with Context if Context updates (Initial Load)
    useEffect(() => {
        if (user) {
            setUserInfo(user);
        }
    }, [user]);

    // Handle resize to detect mobile and force expanded sidebar on mobile
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) {
                setIsCollapsed(false); // Force expanded on mobile
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // --- Effect for fetching Company Data AND refreshing User Data ---
    // This effect runs whenever 'refreshKey' changes (triggered by AdminManager)
    useEffect(() => {
        const loadSidebarData = async () => {
            const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
            if (!storedToken) return;

            // 1. Fetch Company Data (Only set loading for company logo area)
            setIsCompanyLoading(true);
            try {
                const companyResponse = await fetch(GET_COMPANY_DETAILS_ENDPOINT, { headers: { 'Authorization': `Bearer ${storedToken}` } });
                if (companyResponse.ok) {
                    const companyData = await companyResponse.json();
                    const companyList = companyData.message || companyData.messgae;
                    if (Array.isArray(companyList) && companyList.length > 0) {
                        const companyInfo = companyList[0];
                        setCompanyName(companyInfo.companyName || 'Company');
                        if (companyInfo.logoUrl) setCompanyLogoUrl(companyInfo.logoUrl);
                        else if (companyInfo.companyLogo) setCompanyLogoUrl(`data:image/png;base64,${companyInfo.companyLogo}`);
                        setCompanyKeyword(companyInfo.companyKeyword);
                        const rawUrl = companyInfo.websiteUrl;
                        setCompanyWebsiteUrl(rawUrl && rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl || ''}`);
                    }
                } else {
                    setCompanyName('My App');
                }
            } catch (error) {
                console.error("Error fetching company data:", error);
                setCompanyName('My App');
            } finally {
                setIsCompanyLoading(false);
            }

            // 2. Silent User Refresh (Does not trigger loading state to prevent flickering)
            // This ensures if AdminManager updates the name, the sidebar reflects it immediately
            if (userInfo && userInfo.id) {
                try {
                    const userResponse = await fetch(GET_USERS_ENDPOINT, { headers: { 'Authorization': `Bearer ${storedToken}` } });
                    if (userResponse.ok) {
                        const data = await userResponse.json();
                        if (data.userData) {
                            // Find the currently logged-in user in the fresh list
                            const freshUser = data.userData.find(u => String(u.id) === String(userInfo.id));
                            
                            // If we found the user and their name or other details changed
                            if (freshUser) {
                                // Merge fresh data with existing data to update the UI
                                const updatedUser = { ...userInfo, ...freshUser };
                                
                                // Update local display state for immediate feedback
                                setUserInfo(updatedUser);
                                
                                // Update localStorage to persist this change on next reload
                                const storedDetails = localStorage.getItem(USER_DETAILS_KEY);
                                if (storedDetails) {
                                    const parsed = JSON.parse(storedDetails);
                                    // We must update the name inside the nested 'user' object if your storage structure is like { token:..., user: {...} }
                                    // Or if it's flat. Based on AdminManager code: JSON.parse(storedUserDetailsString).role...
                                    // It seems USER_DETAILS_KEY stores the user object directly or an object containing it.
                                    // We will merge safely.
                                    const newDetails = { ...parsed, ...freshUser };
                                    localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(newDetails));
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error refreshing user data silently:", error);
                }
            }
        };

        loadSidebarData();
    }, [refreshKey]); // Triggers when AdminManager calls triggerRefresh()

    const toggleSidebarCollapse = () => {
        if (isMobile) return; // Prevent collapsing on mobile
        setIsCollapsed(prevState => !prevState);
    };

    const toggleProfileDropdown = () => setIsProfileOpen(prevState => !prevState);

    const handleNavLinkClick = (e, path) => {
        if (location.pathname === path) {
            e.preventDefault();
            triggerRefresh();
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        window.close();
        window.location.replace(process.env.REACT_APP_ZTOOL_URL || '/login');
    };
    
    // --- Render Logic ---
    if (!userInfo) {
        return <SidebarSkeleton isCollapsed={isCollapsed} />;
    }

    const LogoContent = () => (<div className="logo">{isCompanyLoading || !companyLogoUrl ? (<span className="logo-initials">{companyName === 'Loading...' ? '' : (companyName || 'A').charAt(0).toUpperCase()}</span>) : (<img src={companyLogoUrl} alt="Company Logo" className="logo-image" />)}{!isCollapsed && <span className="logo-name-text">{companyName}</span>}</div>);
    
    const userRole = userInfo.role?.name?.toUpperCase();
    const isSuperAdmin = userRole === 'SUPER_ADMIN';
    // Use userInfo.name for the avatar and display name to reflect updates immediately
    const avatarInitial = userInfo.name ? userInfo.name.charAt(0).toUpperCase() : 'U';
    
    const menuItems = [ { id: 'dashboard', icon: <IoGridOutline />, text: 'Dashboard', path: '/dashboard' }, { id: 'tasks', icon: <IoCheckboxOutline />, text: 'Tasks', path: '/tasks' }, { id: 'projects', icon: <IoBriefcaseOutline />, text: 'Projects', path: '/Project' }, ];
    if (isSuperAdmin) { menuItems.push({ id: 'assets', icon: <IoConstructOutline />, text: 'Assets', path: '/assets' }); }
    if (isSuperAdmin || userRole === 'USER' || userRole === 'REPORTING MANAGER' || userRole === 'OPERATIONS') { menuItems.push({ id: 'customers', icon: <HiOutlineUserGroup />, text: 'Customers', path: '/customers' }); }
    
    const organizationItems = [];
    if (isSuperAdmin) {
        organizationItems.push({ id: 'team', icon: <IoPeopleOutline />, text: 'Team', path: '/organization/team' });
        organizationItems.push({ id: 'adminManager', icon: <BsPersonGear />, text: 'User Management', path: '/organization/user-management'});
        if (companyKeyword !== 'PL' || (userInfo && userInfo.email && userInfo.email.endsWith('@zasyaonline.com'))) {
            organizationItems.push({ id: 'commonMaster', icon: <IoLayersOutline />, text: 'Common Master', path: '/organization/common-master'});
        }
    }
    if ((isSuperAdmin || userRole === 'REPORTING MANAGER') && companyKeyword === 'ZO') {
        organizationItems.push({ id: 'projectTemplate', icon: <IoFileTrayStackedOutline />, text: 'Project Template', path: '/organization/project-template' });
    }
    organizationItems.push({ id: 'teamMgmt', icon: <HiOutlineUsers />, text: 'Team Management', path: '/organization/team-management' });

    const reportsItems = [ { id: 'reports', icon: <IoDocumentTextOutline />, text: 'Reports', path: '/reports' } ];
    if (isSuperAdmin) {
        if (companyKeyword !== 'PL' || (userInfo && userInfo.email && userInfo.email.endsWith('@zasyaonline.com'))) {
            reportsItems.push({ id: 'reportSettings', icon: <IoBuildOutline />, text: 'Report Setting', path: '/reports/settings' });
        }
    }

    const userSectionItems = [ { id: 'settingsUser', icon: <IoSettingsOutline />, text: 'Settings', path: '/settings' }, ];

    return (
        <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
             <div className="sidebar-header">
                <a href={companyWebsiteUrl} className="logo-link" title={`${companyName} Home`} target="_blank" rel="noopener noreferrer"><LogoContent /></a>
                <div onClick={toggleSidebarCollapse} className="collapse-icon-wrapper" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSidebarCollapse(); }} aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"} title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
                    {isCollapsed ? <IoChevronForward className="collapse-icon" /> : <IoChevronBack className="collapse-icon" />}
                </div>
            </div>
            <div className="sidebar-content">
                <div className="menu-group">{!isCollapsed && <p className="menu-group-title">MENU</p>}<ul>{menuItems.map(item => (<li key={item.id} title={isCollapsed ? item.text : undefined}><NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`} onClick={(e) => handleNavLinkClick(e, item.path)}><span className="menu-item-icon">{item.icon}</span>{!isCollapsed && <span className="menu-item-text">{item.text}</span>}</NavLink></li>))}</ul></div>
                {organizationItems.length > 0 && (<div className="menu-group">{!isCollapsed && <p className="menu-group-title">ORGANIZATION</p>}<ul>{organizationItems.map(item => (<li key={item.id} title={isCollapsed ? item.text : undefined}><NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`} onClick={(e) => handleNavLinkClick(e, item.path)}><span className="menu-item-icon">{item.icon}</span>{!isCollapsed && <span className="menu-item-text">{item.text}</span>}</NavLink></li>))}</ul></div>)}
                {reportsItems.length > 0 && (<div className="menu-group">{!isCollapsed && <p className="menu-group-title">REPORTS</p>}<ul>{reportsItems.map(item => (<li key={item.id} title={isCollapsed ? item.text : undefined}><NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`} onClick={(e) => handleNavLinkClick(e, item.path)}><span className="menu-item-icon">{item.icon}</span>{!isCollapsed && <span className="menu-item-text">{item.text}</span>}</NavLink></li>))}</ul></div>)}
                {userSectionItems.length > 0 && (<div className="menu-group">{!isCollapsed && <p className="menu-group-title">USER</p>}<ul>{userSectionItems.map(item => (<li key={item.id} title={isCollapsed ? item.text : undefined}><NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`} onClick={(e) => handleNavLinkClick(e, item.path)}><span className="menu-item-icon">{item.icon}</span>{!isCollapsed && <span className="menu-item-text">{item.text}</span>}</NavLink></li>))}</ul></div>)}
            </div>
            <div className="sidebar-footer">
                <div className="footer-separator"></div>
                <div className="user-profile-wrapper">{!isCollapsed ? (<div className="user-profile-section"><Link to="/settings/profile" className="avatar-link" title="View Profile"><div className="avatar">{avatarInitial}</div></Link><div className="user-info"><div className="user-info-header" onClick={toggleProfileDropdown} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleProfileDropdown(); }}><span className="user-name">{userInfo.name || 'User'}</span><IoChevronDown className={`profile-dropdown-icon ${isProfileOpen ? 'open' : ''}`} /></div><Link to="/settings/profile" className="user-subtext-link"><span className="user-subtext">Profile • Settings</span></Link><div className={`user-details-container ${isProfileOpen ? 'open' : ''}`}><div className="user-additional-info"><div className="info-item"><span className="info-label">Role:</span><span className="info-value">{userInfo.role?.name?.toUpperCase() || 'N/A'}</span></div><div className="info-item"><span className="info-label">Designation:</span><span className="info-value">{userInfo.designation?.designationName?.toUpperCase() || 'N/A'}</span></div><div className="info-item"><span className="info-label">Department:</span><span className="info-value">{userInfo.department?.departmentName?.toUpperCase() || 'N/A'}</span></div></div></div></div></div>) : (<Link to="/settings/profile" className="user-profile-link" title={`${userInfo.name} - Profile & Settings`}><div className="user-profile-section collapsed-user-profile"><div className="avatar">{avatarInitial}</div></div></Link>)}</div>
                <div onClick={handleLogout} className="logout-section menu-item" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLogout(); }} title={isCollapsed ? "Logout" : "Logout"}><span className="menu-item-icon"><IoLogOutOutline /></span>{!isCollapsed && <span className="menu-item-text">Logout</span>}</div>
            </div>
        </div>
    );
};

export default Sidebar;